// ============================================================================
// CONFIG SETTINGS HOOK
// ============================================================================
// Central state management for the Configuration Settings screen.
//
// DATA: All data here is simulated — replace the SIMULATED_* constants and
// fetch functions with real API calls (e.g. configService.getGroups(),
// configService.getEffectiveConfig(), configService.updateValues()) in your
// application.

import { useState, useCallback, useEffect, useRef } from 'react';
import { notificationService } from 'services/notificationService';
import type {
  ConfigGroup,
  ConfigDefinition,
  ConfigEffectiveValue,
  ConfigValueUpdate,
  ConfigListItem,
} from 'api/types/config.types';

export type ScopeType = 'SYSTEM' | 'ORGANIZATION' | 'DEPARTMENT';

// ============================================================================
// SIMULATED DATA
// Replace these with real API calls in your application.
// ============================================================================

const SIMULATED_GROUPS: ConfigGroup[] = [
  {
    groupKey: 'general',
    displayName: 'General',
    description: 'Core application settings',
    icon: 'settings',
    displayOrder: 1,
  },
  {
    groupKey: 'notifications',
    displayName: 'Notifications',
    description: 'Email, SMS, and alert preferences',
    icon: 'bell',
    displayOrder: 2,
  },
  {
    groupKey: 'security',
    displayName: 'Security',
    description: 'Authentication and access control settings',
    icon: 'lock',
    displayOrder: 3,
  },
  {
    groupKey: 'display',
    displayName: 'Display',
    description: 'UI preferences and display defaults',
    icon: 'users',
    displayOrder: 4,
  },
];

const SIMULATED_DEFINITIONS: Record<string, ConfigDefinition[]> = {
  general: [
    {
      configKey: 'app_name',
      displayName: 'Application Name',
      description: 'Name shown in the browser tab and application header.',
      dataType: 'String',
      defaultValue: 'My App',
      maxScopeLevel: 1,
      displayOrder: 1,
    },
    {
      configKey: 'date_format',
      displayName: 'Date Format',
      description: 'How dates are displayed across the application.',
      dataType: 'String',
      defaultValue: 'DD/MM/YYYY',
      maxScopeLevel: 1,
      displayOrder: 2,
    },
    {
      configKey: 'currency_code',
      displayName: 'Currency Code',
      description: 'ISO 4217 currency code used for monetary values (e.g. USD, EUR, GBP).',
      dataType: 'String',
      defaultValue: 'USD',
      maxScopeLevel: 0,
      displayOrder: 3,
    },
    {
      configKey: 'timezone',
      displayName: 'Timezone',
      description: 'Server timezone used for date/time calculations.',
      dataType: 'String',
      defaultValue: 'UTC',
      maxScopeLevel: 0,
      displayOrder: 4,
    },
  ],
  notifications: [
    {
      configKey: 'email_enabled',
      displayName: 'Email Notifications',
      description: 'Enable or disable all email notifications.',
      dataType: 'Boolean',
      defaultValue: 'true',
      maxScopeLevel: 2,
      displayOrder: 1,
    },
    {
      configKey: 'sms_enabled',
      displayName: 'SMS Notifications',
      description: 'Enable or disable SMS/text message notifications.',
      dataType: 'Boolean',
      defaultValue: 'false',
      maxScopeLevel: 2,
      displayOrder: 2,
    },
    {
      configKey: 'notification_retention_days',
      displayName: 'Notification Retention',
      description: 'How long notifications are kept before automatic deletion.',
      dataType: 'Integer',
      defaultValue: '90',
      unitLabel: 'days',
      minValue: '7',
      maxValue: '365',
      maxScopeLevel: 1,
      displayOrder: 3,
    },
    {
      configKey: 'alert_channels',
      displayName: 'Alert Channels',
      description: 'Channels used to deliver critical system alerts.',
      dataType: 'List',
      maxScopeLevel: 1,
      displayOrder: 4,
    },
  ],
  security: [
    {
      configKey: 'session_timeout_minutes',
      displayName: 'Session Timeout',
      description: 'Idle minutes before the user is automatically logged out.',
      dataType: 'Integer',
      defaultValue: '60',
      unitLabel: 'min',
      minValue: '5',
      maxValue: '480',
      maxScopeLevel: 0,
      displayOrder: 1,
    },
    {
      configKey: 'password_min_length',
      displayName: 'Minimum Password Length',
      description: 'Minimum number of characters required for all user passwords.',
      dataType: 'Integer',
      defaultValue: '8',
      minValue: '6',
      maxValue: '32',
      maxScopeLevel: 0,
      displayOrder: 2,
    },
    {
      configKey: 'max_login_attempts',
      displayName: 'Max Login Attempts',
      description: 'Failed login attempts before the account is temporarily locked.',
      dataType: 'Integer',
      defaultValue: '5',
      minValue: '3',
      maxValue: '10',
      maxScopeLevel: 0,
      displayOrder: 3,
    },
    {
      configKey: 'two_factor_required',
      displayName: 'Require Two-Factor Auth',
      description: 'Force all users to enroll in two-factor authentication.',
      dataType: 'Boolean',
      defaultValue: 'false',
      maxScopeLevel: 0,
      displayOrder: 4,
    },
  ],
  display: [
    {
      configKey: 'default_page_size',
      displayName: 'Default Page Size',
      description: 'Number of rows shown per page in data grids.',
      dataType: 'Integer',
      defaultValue: '25',
      minValue: '10',
      maxValue: '200',
      maxScopeLevel: 2,
      displayOrder: 1,
    },
    {
      configKey: 'show_welcome_banner',
      displayName: 'Show Welcome Banner',
      description: 'Display a welcome banner on the dashboard for new sessions.',
      dataType: 'Boolean',
      defaultValue: 'true',
      maxScopeLevel: 2,
      displayOrder: 2,
    },
  ],
};

// --- Simulated effective value resolution ---

const getSystemEffectiveValues = (groupKey: string): ConfigEffectiveValue[] => {
  const defs = SIMULATED_DEFINITIONS[groupKey] ?? [];
  return defs.map((def) => {
    if (def.dataType === 'List') {
      return {
        configKey: def.configKey,
        listValues: [
          { value: 'EMAIL', isActive: true },
          { value: 'IN_APP', isActive: true },
          { value: 'WEBHOOK', isActive: false },
        ],
        resolvedFromScopeType: 'SYSTEM',
      };
    }
    return {
      configKey: def.configKey,
      effectiveValue: def.defaultValue,
      resolvedFromScopeType: 'SYSTEM',
    };
  });
};

const getOrganizationEffectiveValues = (
  groupKey: string,
  orgId: string
): ConfigEffectiveValue[] => {
  return getSystemEffectiveValues(groupKey).map((ev) => {
    // Simulate org-1 overriding app_name and default_page_size
    if (orgId === 'org-1' && ev.configKey === 'app_name') {
      return { ...ev, effectiveValue: 'Alpha Portal', resolvedFromScopeType: 'ORGANIZATION' };
    }
    if (orgId === 'org-1' && ev.configKey === 'default_page_size') {
      return { ...ev, effectiveValue: '50', resolvedFromScopeType: 'ORGANIZATION' };
    }
    // Simulate org-2 disabling email notifications
    if (orgId === 'org-2' && ev.configKey === 'email_enabled') {
      return { ...ev, effectiveValue: 'false', resolvedFromScopeType: 'ORGANIZATION' };
    }
    return ev; // inherited from SYSTEM
  });
};

const getDepartmentEffectiveValues = (
  groupKey: string,
  orgId: string,
  deptId: string
): ConfigEffectiveValue[] => {
  return getOrganizationEffectiveValues(groupKey, orgId).map((ev) => {
    // Simulate dept-1 overriding default_page_size
    if (deptId === 'dept-1' && ev.configKey === 'default_page_size') {
      return { ...ev, effectiveValue: '10', resolvedFromScopeType: 'DEPARTMENT' };
    }
    // Simulate dept-2 hiding the welcome banner
    if (deptId === 'dept-2' && ev.configKey === 'show_welcome_banner') {
      return { ...ev, effectiveValue: 'false', resolvedFromScopeType: 'DEPARTMENT' };
    }
    return ev; // inherited from organization or system
  });
};

const delay = (ms: number) => new Promise<void>((resolve) => setTimeout(resolve, ms));

// ============================================================================
// HOOK
// ============================================================================

export const useConfigSettings = () => {
  const [groups, setGroups] = useState<ConfigGroup[]>([]);
  const [groupsLoading, setGroupsLoading] = useState(false);
  const [activeGroupKey, setActiveGroupKey] = useState<string | null>(null);
  const [scopeType, setScopeType] = useState<ScopeType>('SYSTEM');
  const [selectedOrganizationId, setSelectedOrganizationId] = useState<string | null>(null);
  const [selectedDepartmentId, setSelectedDepartmentId] = useState<string | null>(null);
  const [effectiveValues, setEffectiveValues] = useState<ConfigEffectiveValue[]>([]);
  const [definitions, setDefinitions] = useState<ConfigDefinition[]>([]);
  const [editedValues, setEditedValues] = useState<Record<string, ConfigValueUpdate>>({});
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<Error | null>(null);

  const hasChanges = Object.keys(editedValues).length > 0;
  const autoSelectedRef = useRef(false);

  // Fetch groups on mount
  const fetchGroups = useCallback(async () => {
    setGroupsLoading(true);
    try {
      await delay(400);
      const sorted = [...SIMULATED_GROUPS].sort((a, b) => a.displayOrder - b.displayOrder);
      setGroups(sorted);
      if (sorted.length > 0 && !autoSelectedRef.current) {
        autoSelectedRef.current = true;
        setActiveGroupKey(sorted[0].groupKey);
      }
    } catch (err) {
      setError(err as Error);
    } finally {
      setGroupsLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchGroups();
  }, [fetchGroups]);

  // Fetch definitions + effective values whenever group / scope / context changes
  const fetchGroupData = useCallback(
    async (
      groupKey: string,
      scope: ScopeType,
      orgId: string | null,
      deptId: string | null
    ) => {
      setLoading(true);
      setError(null);
      try {
        await delay(250);
        const defs = (SIMULATED_DEFINITIONS[groupKey] ?? [])
          .slice()
          .sort((a, b) => a.displayOrder - b.displayOrder);
        setDefinitions(defs);

        let evs: ConfigEffectiveValue[];
        if (scope === 'ORGANIZATION' && orgId) {
          evs = getOrganizationEffectiveValues(groupKey, orgId);
        } else if (scope === 'DEPARTMENT' && orgId && deptId) {
          evs = getDepartmentEffectiveValues(groupKey, orgId, deptId);
        } else {
          evs = getSystemEffectiveValues(groupKey);
        }
        setEffectiveValues(evs);
      } catch (err) {
        setError(err as Error);
      } finally {
        setLoading(false);
      }
    },
    []
  );

  useEffect(() => {
    if (!activeGroupKey) return;
    if (scopeType === 'ORGANIZATION' && !selectedOrganizationId) {
      setEffectiveValues([]);
      setDefinitions([]);
      return;
    }
    if (scopeType === 'DEPARTMENT' && (!selectedOrganizationId || !selectedDepartmentId)) {
      if (!selectedOrganizationId) {
        setEffectiveValues([]);
        setDefinitions([]);
      }
      return;
    }
    setEditedValues({});
    fetchGroupData(activeGroupKey, scopeType, selectedOrganizationId, selectedDepartmentId);
  }, [activeGroupKey, scopeType, selectedOrganizationId, selectedDepartmentId, fetchGroupData]);

  // Auto-downgrade scope if the selected group doesn't support DEPARTMENT level
  useEffect(() => {
    if (scopeType === 'DEPARTMENT' && definitions.length > 0) {
      const hasDeptLevel = definitions.some((d) => d.maxScopeLevel >= 2);
      if (!hasDeptLevel) {
        setScopeType('ORGANIZATION');
        setSelectedDepartmentId(null);
      }
    }
  }, [definitions, scopeType]);

  const updateValue = useCallback(
    (configKey: string, value?: string, listValues?: ConfigListItem[]) => {
      setEditedValues((prev) => ({
        ...prev,
        [configKey]: { configKey, value, listValues },
      }));
    },
    []
  );

  const resetValue = useCallback((configKey: string) => {
    setEditedValues((prev) => ({
      ...prev,
      [configKey]: { configKey, value: undefined, listValues: undefined },
    }));
  }, []);

  const discardChanges = useCallback(() => {
    setEditedValues({});
  }, []);

  const saveChanges = useCallback(async () => {
    if (!activeGroupKey || !hasChanges) return;
    setSaving(true);
    try {
      await delay(600);
      await fetchGroupData(activeGroupKey, scopeType, selectedOrganizationId, selectedDepartmentId);
      setEditedValues({});
      notificationService.success('Settings Saved', 'Configuration settings saved successfully.');
    } catch (err) {
      const e = err as Error;
      setError(e);
      notificationService.error('Save Failed', e.message || 'Failed to save configuration settings.');
    } finally {
      setSaving(false);
    }
  }, [activeGroupKey, hasChanges, scopeType, selectedOrganizationId, selectedDepartmentId, fetchGroupData]);

  const selectGroup = useCallback((groupKey: string) => {
    setActiveGroupKey(groupKey);
  }, []);

  const changeScopeType = useCallback((newScope: ScopeType) => {
    setScopeType(newScope);
    if (newScope === 'SYSTEM') {
      setSelectedOrganizationId(null);
      setSelectedDepartmentId(null);
    }
    if (newScope === 'ORGANIZATION') {
      setSelectedDepartmentId(null);
    }
  }, []);

  const selectOrganization = useCallback((orgId: string | null) => {
    setSelectedOrganizationId(orgId);
    setSelectedDepartmentId(null);
  }, []);

  const selectDepartment = useCallback((deptId: string | null) => {
    setSelectedDepartmentId(deptId);
  }, []);

  return {
    groups,
    groupsLoading,
    activeGroupKey,
    scopeType,
    selectedOrganizationId,
    selectedDepartmentId,
    effectiveValues,
    definitions,
    editedValues,
    hasChanges,
    loading,
    saving,
    error,
    selectGroup,
    changeScopeType,
    selectOrganization,
    selectDepartment,
    updateValue,
    resetValue,
    discardChanges,
    saveChanges,
  };
};
